import {useUpmPlantsContext} from '@hconnect/upm'
import {buildFeatureUrl} from '@hproduce/suite-core'
import {useCallback, useEffect, useMemo} from 'react'
import {useNavigate, useSearchParams} from 'react-router'

import type {RcfaCreatePlantOption} from './useRcfaCreatePlantOptions'

interface UseCreateAnalysisPlantRoutingProperties {
  itemDescription: string
  selectedStoppagePlantId?: string
  plantOptions: RcfaCreatePlantOption[]
  isLoadingPlantOptions: boolean
  plantUpmId: string
  onNavigateToPlant?: (
    plant: RcfaCreatePlantOption,
    options: {preservedDescription: string; searchParams: URLSearchParams}
  ) => Promise<void> | void
  onGoToStoppageList?: () => Promise<void> | void
  /** Called before any plant navigation — use to reset form state */
  onBeforeNavigateToPlant?: () => void
}

interface UseCreateAnalysisPlantRoutingResult {
  availablePlantOptions: RcfaCreatePlantOption[]
  selectedPlantOption: RcfaCreatePlantOption | null
  isRoutePlantAuthorized: boolean
  validatedPlantUpmId: string
  isPlantScopedInputDisabled: boolean
  navigateToPlant: (plant: RcfaCreatePlantOption) => Promise<void>
  goToStoppageList: () => Promise<void>
}

export const useCreateAnalysisPlantRouting = ({
  itemDescription,
  selectedStoppagePlantId,
  plantOptions,
  isLoadingPlantOptions,
  plantUpmId,
  onNavigateToPlant,
  onGoToStoppageList,
  onBeforeNavigateToPlant
}: UseCreateAnalysisPlantRoutingProperties): UseCreateAnalysisPlantRoutingResult => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const upmPlants = useUpmPlantsContext()

  const stoppagePlantOption = useMemo(() => {
    if (!selectedStoppagePlantId) return null

    const stoppagePlant = upmPlants[selectedStoppagePlantId]

    if (!stoppagePlant?.upmId) return null

    return {
      plantId: selectedStoppagePlantId,
      plantName: stoppagePlant.name || selectedStoppagePlantId,
      plantUpmId: stoppagePlant.upmId
    }
  }, [selectedStoppagePlantId, upmPlants])

  const availablePlantOptions = useMemo(() => {
    if (!stoppagePlantOption) return plantOptions

    return plantOptions.some((option) => option.plantUpmId === stoppagePlantOption.plantUpmId)
      ? plantOptions
      : [stoppagePlantOption, ...plantOptions]
  }, [plantOptions, stoppagePlantOption])

  const selectedPlantOption = useMemo(
    () => availablePlantOptions.find((option) => option.plantUpmId === plantUpmId) ?? null,
    [availablePlantOptions, plantUpmId]
  )

  const isRoutePlantAuthorized = Boolean(selectedStoppagePlantId || selectedPlantOption)
  const validatedPlantUpmId = selectedStoppagePlantId
    ? plantUpmId
    : (selectedPlantOption?.plantUpmId ?? '')
  const isPlantScopedInputDisabled =
    isLoadingPlantOptions || (!selectedStoppagePlantId && !selectedPlantOption)

  useEffect(() => {
    if (selectedStoppagePlantId || isLoadingPlantOptions || availablePlantOptions.length === 0) {
      return
    }

    if (selectedPlantOption) return

    const search = searchParams.toString()

    void navigate(
      `${buildFeatureUrl('RcfaList', availablePlantOptions[0].plantUpmId)}/new${search ? `?${search}` : ''}`,
      {
        replace: true,
        state: {preservedDescription: itemDescription}
      }
    )
  }, [
    availablePlantOptions,
    isLoadingPlantOptions,
    itemDescription,
    navigate,
    searchParams,
    selectedPlantOption,
    selectedStoppagePlantId
  ])

  const navigateToPlant = useCallback(
    async (plant: RcfaCreatePlantOption) => {
      if (plant.plantUpmId === plantUpmId) return

      const nextSearchParams = new URLSearchParams(searchParams)
      const search = nextSearchParams.toString()

      onBeforeNavigateToPlant?.()

      if (onNavigateToPlant) {
        await onNavigateToPlant(plant, {
          preservedDescription: itemDescription,
          searchParams: nextSearchParams
        })
        return
      }

      void navigate(
        `${buildFeatureUrl('RcfaList', plant.plantUpmId)}/new${search ? `?${search}` : ''}`,
        {
          state: {preservedDescription: itemDescription}
        }
      )
    },
    [
      itemDescription,
      navigate,
      onBeforeNavigateToPlant,
      onNavigateToPlant,
      plantUpmId,
      searchParams
    ]
  )

  const goToStoppageList = useCallback(async () => {
    if (onGoToStoppageList) {
      await onGoToStoppageList()
      return
    }

    void navigate(buildFeatureUrl('StoppageList', plantUpmId))
  }, [navigate, onGoToStoppageList, plantUpmId])

  return {
    availablePlantOptions,
    selectedPlantOption,
    isRoutePlantAuthorized,
    validatedPlantUpmId,
    isPlantScopedInputDisabled,
    navigateToPlant,
    goToStoppageList
  }
}
